import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || ''
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || ''

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn(
    '[commons] VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY is not set — ' +
      'auth will not work. Copy .env.example to .env.local and fill in values.',
  )
}

/**
 * A2F-633: snapshot the password-recovery tokens from the URL hash
 * BEFORE `createClient` runs (createClient schedules an async
 * `_initialize()` that will consume and clear `window.location.hash`).
 *
 * Why we need a snapshot even though the SDK auto-detects the hash:
 *
 * Supabase's `_getSessionFromURL` internally calls `_getUser(access_token)`
 * against `{supabaseUrl}/auth/v1/user` before saving the session
 * (@supabase/auth-js GoTrueClient.js line ~3074). If that call fails for
 * ANY reason — transient network hiccup, rate limit, an infra edge case
 * where the recovery token hasn't propagated yet — the SDK's catch
 * returns an error result (line ~3092), the session is NEVER saved, and
 * `PASSWORD_RECOVERY` is NEVER fired. By the time the user submits the
 * form, `updateUser` → `_useSession` returns a null session and throws
 * `AuthSessionMissingError`, which surfaces to the user as the generic
 * "reset link may have expired" message.
 *
 * With this snapshot, `UpdatePasswordPage` can recover by calling
 * `supabase.auth.setSession(RECOVERY_HASH_TOKENS)` before
 * `updateUser` — a second chance at establishing the session that
 * isn't coupled to the SDK's one-shot init.
 *
 * Exported as a module-level const (not a function) because the capture
 * MUST happen exactly once, at module load, before the SDK is created.
 * Later reads of `window.location.hash` may find it empty.
 */
export const RECOVERY_HASH_TOKENS: {
  access_token: string
  refresh_token: string
} | null = (() => {
  if (typeof window === 'undefined') return null
  const hash = window.location.hash
  if (!hash.startsWith('#')) return null
  const params = new URLSearchParams(hash.slice(1))
  const access_token = params.get('access_token')
  const refresh_token = params.get('refresh_token')
  const type = params.get('type')
  // Only snapshot for recovery flows — avoid capturing signup /
  // magic-link / OAuth hash tokens that the normal SDK path handles.
  if (access_token && refresh_token && type === 'recovery') {
    return { access_token, refresh_token }
  }
  return null
})()

// A2F-633: pin flowType to 'implicit' so a future @supabase/supabase-js
// bump doesn't silently flip the default to PKCE. PKCE delivers
// `?code=…` query params instead of `#access_token=…` hash fragments,
// and nothing in commons calls `exchangeCodeForSession` — a silent
// switch would break password reset entirely.
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    flowType: 'implicit',
  },
})
